import { useEffect, useState } from 'react';
import { Table, SimpleGrid, Text, Stack, Paper, Group, Button } from '@mantine/core';
import { IconAdd as IconPlus, IconCustomer } from '../../components/icons/CncIcons';
import EntityHeader from '../../components/EntityHeader';
import { ViewMode } from '../../components/TableGridToggle';
import { api, imageUrl } from '../../api/client';
import CustomerModal from './CustomerModal';

export interface Customer {
  id: number;
  name: string;
  legalName: string | null;
  logoPath: string | null;
  address: {
    country: string | null;
    county: string | null;
    street: string | null;
    zip: string | null;
  };
  legal: {
    companyName: string | null;
    vatNumber: string | null;
    country: string | null;
    county: string | null;
    street: string | null;
    zip: string | null;
  };
}

function locationOf(c: Customer): string {
  const bits = [c.address.county, c.address.country].filter((x): x is string => !!x && x.trim().length > 0);
  return bits.join(', ');
}

export default function CustomersPage() {
  const [view, setView] = useState<ViewMode>('table');
  const [rows, setRows] = useState<Customer[]>([]);
  const [editing, setEditing] = useState<Customer | 'new' | null>(null);

  async function refresh() {
    const list = await api.get<Customer[]>('/customers');
    setRows(list);
  }

  useEffect(() => { refresh(); }, []);

  return (
    <Stack>
      <EntityHeader title="Customers" icon={<IconCustomer size={26} />} view={view} onView={setView} />

      {view === 'table' ? (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Logo</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: 320 }}>Location</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} className="click-row" onClick={() => setEditing(r)}>
                  <Table.Td>
                    <div className="image-box">
                      {r.logoPath && <img src={imageUrl(r.logoPath, true)} alt={r.name} />}
                    </div>
                  </Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>
                    {locationOf(r) || <Text span c="dimmed">—</Text>}
                  </Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')} data-tour="customers-add">
                    Add Customer
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {rows.map((r) => (
            <div key={r.id} className="grid-card" onClick={() => setEditing(r)}>
              <div className="card-image">
                {r.logoPath ? <img src={imageUrl(r.logoPath)} alt={r.name} /> : <Text c="dimmed" size="xs">No logo</Text>}
              </div>
              <Group justify="space-between">
                <Text fw={600}>{r.name}</Text>
              </Group>
              <Text size="sm" c="dimmed">{locationOf(r) || '—'}</Text>
            </div>
          ))}
          <div className="add-card" onClick={() => setEditing('new')}>
            <Group gap={6}><IconPlus size={16} /><Text>Add Customer</Text></Group>
          </div>
        </SimpleGrid>
      )}

      {editing && (
        <CustomerModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await refresh(); setEditing(null); }}
          onDeleted={async () => { await refresh(); setEditing(null); }}
        />
      )}
    </Stack>
  );
}
